import { Card, Statistic, Typography } from "antd";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { SectionIcon } from "./SectionIcon";

/**
 * Плитка сводки: иконка, подпись, число.
 *
 * Цветная полоса сверху и кружок с иконкой — единственное, что отличает
 * плитки друг от друга: числа на сводке крупные и одинаковые по форме, и без
 * цветовой метки взгляд каждый раз перечитывает подписи.
 *
 * `accent` подсвечивает значение, когда оно требует действия (очередь заявок
 * не пуста). Цвет берётся из токенов темы, а не из палитры раздела: «есть
 * работа» — это состояние, а не опознавательный знак.
 */
export function StatCard({
  color,
  icon,
  title,
  value,
  suffix = null,
  hint = null,
  to = null,
  accent = false,
}: {
  color: string;
  icon: ReactNode;
  title: ReactNode;
  value: string | number;
  /** Короткий хвост в строке значения — «/ 5». Длинный текст сюда не влезет. */
  suffix?: ReactNode;
  /** Пояснение отдельной строкой под значением. */
  hint?: ReactNode;
  /** Куда ведёт плитка. Без ссылки карточка просто не кликается. */
  to?: string | null;
  accent?: boolean;
}) {
  const body = (
    <Card
      hoverable={to !== null}
      styles={{ body: { display: "flex", alignItems: "center", gap: 12 } }}
      style={{ height: "100%", overflow: "hidden", borderTop: `2px solid ${color}` }}
    >
      <SectionIcon color={color} size={38}>
        {icon}
      </SectionIcon>
      {/* `minWidth: 0` обязателен: без него длинное число распирает флекс-строку
          и сталкивает иконку под значение вместо переноса самого текста. */}
      <div style={{ minWidth: 0 }}>
        <Statistic
          title={title}
          value={value}
          suffix={suffix}
          styles={
            accent
              ? { content: { color: "var(--ant-color-warning)", fontWeight: 600 } }
              : { content: { fontWeight: 600 } }
          }
        />
        {hint !== null && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Typography.Text>
        )}
      </div>
    </Card>
  );

  if (to === null) return body;
  // Ссылка снаружи карточки, а не вокруг `Statistic`: иначе кликабельно
  // только само число, а мимо него — мёртвая зона в половину плитки.
  return (
    <Link to={to} style={{ display: "block", height: "100%", color: "inherit" }}>
      {body}
    </Link>
  );
}
